/**
 * The one place a role's rights are put into words. Rendered by the Designer
 * editor while authoring and by the App role card afterwards, so both read
 * the same sentence.
 */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import { summarizeRights, OP_LABELS, type RightsDescription } from './describeRights'

export interface RightsSummaryProps {
  description: RightsDescription
  showDetail?: boolean
}

export function RightsSummary({ description, showDetail = false }: RightsSummaryProps) {
  const [open, setOpen] = useState(false)
  const hasDetail = description.base !== 'full_access' && description.exceptions.length > 0

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1">{summarizeRights(description)}</Typography>
        {description.designerAccess && <Chip size="small" label="Designer access" />}
        {description.userAdmin && <Chip size="small" label="User administration" />}
      </Box>

      {showDetail && description.base === 'full_access' && (
        <Typography variant="caption" color="text.secondary">
          Every object is granted — nothing to list.
        </Typography>
      )}

      {showDetail && hasDetail && (
        <>
          <Button size="small" sx={{ mt: 0.5 }} onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide details' : 'Show details'}
          </Button>
          {open && (
            <Table size="small" sx={{ mt: 1, maxWidth: 520 }}>
              <TableBody>
                {description.exceptions.map((ex) => (
                  <TableRow key={ex.objectName}>
                    <TableCell sx={{ fontWeight: 500 }}>{ex.objectName}</TableCell>
                    <TableCell>{ex.ops.map((op) => OP_LABELS[op]).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Box>
  )
}
